/**
 * A class that encapsulates all the direct interaction with the underlaying engine.
 * Responsible for loading, effects and lower level tasks.
 */

import { SceneGenerator } from '../../../Service/SceneGenerator';
import { RingModel } from '../../../Model/RingModel';
import { MathUtils as THREEMathUtils, PlaneGeometry, Mesh, MeshBasicMaterial, Box3, PerspectiveCamera, WebGLRenderTarget, ShaderMaterial, Vector2, Vector3, Euler } from "three";
import * as THREE from "three";
import { IFRAssetProvider } from './IFRAssetProvider';
import { AssetInfoTypes, Configuration, ConfigurationMapping, Configurator, CreateConfigurationMapping, Engine, IAsset, IMaterial } from '@viscircle-org/threngine';
import { ScreenshotRequest } from '@viscircle-org/threngine/dist/types/src/core/render/screenshotHandler';
import { CustomInputSimple } from '../../SimpleCustomInput';


export interface IRenderEngine {
  settings:any,
  configurator:Configurator,

  fadeIn(color:string, time:number):Promise<void>,
  fadeOut(color:string, time:number):Promise<void>,

  initEngine(mount:HTMLDivElement, ring:RingModel[]):Promise<any>,
}

export class RenderEngine implements IRenderEngine {
  public engine:Engine;
  private _assetProvider:IFRAssetProvider;
  private _mappings:ConfigurationMapping
  private _materials:{name:string, id:string}[];
  private _configurator:Configurator;
  public reflectionPlane:Mesh = null;
  private _shadowPlane:Mesh = null;
  private _autoRotationActive:boolean = true;
  private _autoRotationIntervalId = null;
  private _customInput:CustomInputSimple;
  public autoRotationDelay = 0;
  public autoRotationSpeed = 0;

  private _zoomSensitivity:number = 0.15;

  get settings() { return this.engine.services.render.settings };
  get configurator():Configurator { return this._configurator; };
  get assetProvider():IFRAssetProvider { return this._assetProvider };
  get mappings():ConfigurationMapping { return this._mappings };
  get customInput():CustomInputSimple{return this._customInput};

  private _reflectionVisible:boolean = true;
  get reflectionVisible():boolean { return this._reflectionVisible };
  set reflectionVisible(setTo:boolean) { this._reflectionVisible = setTo };

  get shadowVisible():boolean { return this._shadowPlane.visible };
  set shadowVisible(setTo:boolean) { this._shadowPlane.visible = setTo };

  get hasBackground():boolean { 
    if (this.engine) return this.engine.services.render.scene.background !== null;
    return true;
  };

  fadeIn = (color:string, time:number) => this.engine.services.render.fadeIn(time, color);
  fadeOut = (color:string, time:number) => this.engine.services.render.fadeOut(time, color);

  initEngine = (mount:HTMLDivElement, rings:RingModel[]):Promise<any> => {
    return this._initialize(mount, rings);
  };

  private _initialize = async (mount:HTMLDivElement, rings:RingModel[]):Promise<any> => {
    //console.log("RenderEngine._initialize() rings argument", rings);

    this._assetProvider = new IFRAssetProvider();
    this.engine = new Engine(this._assetProvider);

    // console.log("engine", this.engine);

    await this.engine.initialize(mount);

    // const renderer = this.engine.services.render.renderController.renderer;
    // renderer.setPixelRatio(1); //Limit DPR on some device (i.e. Macs with Retina screens) this will improve performance in exchange for somewhat lower quality
    
    (window as any).VCThreeDConfigurator.onResize = this.onResize;
    this.onResize();
    
    const project = this._assetProvider.project;
    const config:Configuration = project.config;

    //renderSettings
    // console.log("project", project);
    const defaultRenderSettings = this._assetProvider.getAsset(config.default_state.render_settings).info.data;
    // console.log("defaultRenderSettings", defaultRenderSettings);
    this.engine.services.render.settings.setSettings(defaultRenderSettings);
    // console.log("renderSettings", this.engine.services.render.settings.getSettings());
    
    const assets:IAsset[] = this._assetProvider.getAssets();
    this._generateMaterialLibrary(assets);
    
    const sceneGenerator = new SceneGenerator(this.engine, assets, config, rings);
    const sceneConfig = await sceneGenerator.generateScene();
    
    // console.log('SceneGenerator.config', sceneConfig);
    
    this.engine.services.render.fadeOut(0, "white");
    this._configurator = new Configurator(sceneConfig, this.engine);
    
    // console.log("configurator", this._configurator);
    
    const scene = await this._configurator.initialize();
    
    //Overwrite threngine tone mapping
    this._enableThreeTonemapping();

    //remove morph normals
    await this.configurator.setVisible(this.configurator.config.groupings.map((g) => g.id), true);
    scene.traverse(c => {
      if (c instanceof Mesh && c.geometry instanceof THREE.BufferGeometry && c.geometry.morphAttributes.normal) {
        delete c.geometry.morphAttributes.normal;
      }
    })

    this.engine.services.render.setScene(scene);
    // console.log("SCENE", scene);

    this.setBackground(new THREE.Color( 1, 1, 1 ));

    await this.configurator.setVisible(this.configurator.config.groupings.map((g) => g.id), true);

    const urlParams = new URLSearchParams(window.location.search);
    if (!(urlParams.has('mode') && urlParams.get('mode') === 'material-image-capture')) {
      this._initializeAOShadow();
      this._initializePlanarReflection();
    };

    await this.engine.services.render.fadeIn(1000, "white");
    //console.log("Configurator: ", configurator);

    //Prevent scaling of entire viewport on iOS 10+
    (this.engine as any)._mount.addEventListener('touchmove', (event) => {
      if (event.scale !== 1) { event.preventDefault(); }
    }, false);
    //--

    this.updateZoomSensitivity();

    this._mappings = CreateConfigurationMapping(sceneConfig, this._assetProvider.getAssets());
    // console.log("MAPPINGS", this._mappings);
    this._customInput = new CustomInputSimple(this.engine.services.render.domContainer.canvas)

    return Promise.resolve(null);
  };

  private _enableThreeTonemapping = () => {
    const {renderer, composer} = this.engine.services.render.renderController;

    //Disable threngine tonemapping
    const envInfo = this.engine.services.environment.environment.info;
    envInfo.data.tonemappingEnable = false;
    this.engine.services.environment.setEnvironmentInfo(envInfo);

    THREE.ShaderChunk['tonemapping_pars_fragment'] = `
      #ifndef saturate
      // <common> may have defined saturate() already
      #define saturate( a ) clamp( a, 0.0, 1.0 )
      #endif

      #define toneMappingExposure 3.0
      #define toneMappingWhitePoint 6.0

      // source: http://filmicgames.com/archives/75
      #define Uncharted2Helper( x ) max( ( ( x * ( 0.15 * x + 0.10 * 0.50 ) + 0.20 * 0.02 ) / ( x * ( 0.15 * x + 0.50 ) + 0.20 * 0.30 ) ) - 0.02 / 0.30, vec3( 0.0 ) )
      vec3 CustomToneMapping( vec3 color ) {
        // John Hable's filmic operator from Uncharted 2 video game
        color *= toneMappingExposure;
        return Uncharted2Helper( color ) / Uncharted2Helper( vec3( toneMappingWhitePoint ) ) ;
      }
    `;

    renderer.toneMapping = THREE.CustomToneMapping;

    const {tonemappass} = composer;

    if ((<any>tonemappass).material) {
      (<any>tonemappass).material.fragmentShader = `void main() {gl_FragColor = vec4(0.0);}`;
    }
  };

  public onResize = () => {
    const engine = this.engine as any;
    if (!engine || !engine._onResize) {
      console.warn("Engine.onResize() called, but engine is not yet initiated. Aborting!");
      return;
    }
    engine._onResize();
  }

  private _generateMaterialLibrary = (assets:IAsset[]) => {
    const materialAssets = assets.filter(a => a.infoType === 'MATERIAL') as IMaterial[];
    this._materials = materialAssets.map((ma) => { return {name: ma.info.name, id: ma.id }});
  }// /_generateMaterialLibrary()

  public changeMaterial = async (zone:string, materialName:string) => {
    const groupings = this._configurator.config.groupings.filter(g => g.name === zone);
    const material = this._materials.find((mat) => mat.name === materialName);

    console.log("grouping", groupings)

    if (!material) console.error("Material not found!", materialName);
    if (!groupings) console.error("No groupings found!", zone);
    if (!material || !groupings || groupings.length===0) return;

    groupings.forEach(async (grouping) => {
      await this._configurator.setMaterial(grouping.id, material.id)
      
      const meshes = await this._configurator._getMeshes(grouping);

      meshes.forEach((mesh) => {
        const material = mesh.material as THREE.MeshPhysicalMaterial;
        material.needsUpdate = true;
      });
    });
  }

  public getScreenShot = (req:ScreenshotRequest):Promise<Blob> => {
    return this.engine.services.render.screenshot(req);
  }

  public downloadScreenshot = async (req:ScreenshotRequest) => {
    const shotBlob = await this.getScreenShot(req)
    const image = new Image();
    image.src = URL.createObjectURL(shotBlob);
    const link = document.createElement('a');
    link.href = image.src;
    link.target = '_blank';
    link.download = 'screenshot ' + new Date() + '.png';
    link.click();
  };// /downloadScreenshot()

  public downloadCheckoutScreenshot = async (resetCameraAndRotationWhenDone:boolean, size?:number) => {
    const shotBlob = await this.getCheckoutScreenshot(resetCameraAndRotationWhenDone, size, true)
    const image = new Image();
    image.src = URL.createObjectURL(shotBlob);
    const link = document.createElement('a');
    link.href = image.src;
    link.target = '_blank';
    link.download = 'screenshot ' + new Date() + '.png';
    link.click();
  };

  public getCheckoutScreenshot = (resetCameraAndRotationWhenDone:boolean, size?:number, blob?:boolean):Promise<any> => {

    const currentCameraPosition = new Vector3().copy(this.engine.services.render.cameraController.cameraPosition);
    const currentSceneRotation = new Euler().copy(this.engine.services.render.scene.rotation);
    const autoRotationActive = this._autoRotationActive;
    
    const screenshotCameraPosition = new Vector3(0.043, 0.055, 0.069);
    const screenshotSize = {w: size?size:500, h: size?size:500};

    return new Promise((resolve, reject) => {

      const oldSize = (this.engine as any)._getSize();

      this.engine.services.render.fadeOut(0, 'white');
      this.engine.services.render.resize(screenshotSize.w, screenshotSize.h);

      this.engine.services.render.camera.position.copy(screenshotCameraPosition);

      this.stopAutoRotation();
      this.engine.services.render.scene.setRotationFromEuler(new Euler(0, 0, 0, 'XYZ'));

      const callback = () => {
        this.getScreenShot({})
        .then((screenshotBlob) => {
          this.engine.services.render.resize(oldSize.w, oldSize.h);
          this.engine.services.render.fadeIn(1500, 'white');

          const image = new Image();
          image.src = URL.createObjectURL(screenshotBlob);
          image.addEventListener('load', (e) => {
            const canvas = document.createElement('canvas');
            const canvasContext = canvas.getContext('2d');
            canvas.width = image.width;
            canvas.height = image.height;

            canvasContext.drawImage(
              image,
              0,                                //sx
              0,                                //sy
              image.width,                      //sWidth
              image.height,                     //sHeight
              0,                                //dx
              0,                                //dy
              canvas.width,                     //dWidth
              canvas.height,                    //dHeight
            );

            const dataUrl = canvas.toDataURL('image/png');

            if (resetCameraAndRotationWhenDone) {
              this.engine.services.render.camera.position.copy(currentCameraPosition);
              this.engine.services.render.scene.setRotationFromEuler(currentSceneRotation);
              if (autoRotationActive) {
                this.autoRotationDelay = 0;
                this.initializeAutoRotation();
              }
            };

            if(blob){
              resolve(screenshotBlob)
            }
            else{
              resolve(dataUrl);
            }
          });
        });
      }// /const callback

      let frameCounter = 0;

      this.engine.services.render.scene.onAfterRender = () => {
        frameCounter += 1;
        if (frameCounter >= 10) {
          this.engine.services.render.scene.onAfterRender = ()=>null;
          callback();
        }
      };

    });
  }// /()

  public setBackground = (backgroundColor:THREE.Color):void => {
    this.engine.services.render.scene.background = backgroundColor;
  };

  public updateLowestPointOfRing = async (SKU, minThickness, currentThickness) => {
    let meshes = await this.engine.services.render.getAllMeshesOfCurrentScene();
    meshes = meshes.filter((m) => m.parent && m.parent.name === SKU);
    
    let minY = 0;

    //Find lowest point of ring without any morphTargetInfluences, i.e. all of them at 0
    for (const mesh of meshes) {
      const geometry = mesh.geometry as THREE.BufferGeometry;
      const position = geometry.getAttribute('position');
      const ownScale = mesh.scale.y;
      const parentScale = mesh.parent.scale.y;

      for (let i = 0; i < position.count; i++) {
        const y = position.getY(i) * ownScale * parentScale * 1000;
        if (y < minY) minY = y;
      };
    };

    minY = minY;

    const radius = minY - (currentThickness - minThickness);
    const shadowRadius = -10.94999;

    this.reflectionPlane.position.y = radius/1000.0;
    this._shadowPlane.position.y = this.reflectionPlane.position.y + 0.00025;
    this._shadowPlane.scale.z = radius/shadowRadius;
  }

  public updateWidthOfRing = (width) => {
    const shadowWidth = 8.5;
    if (this._shadowPlane) this._shadowPlane.scale.x = width/shadowWidth;
  };// /updateWidthOfRing()

  public initializeAutoRotation = () => {
    this._autoRotationActive = true;

    try {
      const scene = this.engine.services.render.scene;
      const mount = (this.engine as any)._mount as HTMLElement;

      window.setTimeout(() => {
        if (this._autoRotationActive) {
          this._autoRotationIntervalId = window.setInterval(() => {
            scene.rotateY(THREEMathUtils.degToRad(this.autoRotationSpeed));
          }, 33);
        };
      }, this.autoRotationDelay);

      mount.addEventListener('click', this.stopAutoRotation);
      mount.addEventListener('touchstart', this.stopAutoRotation);
    } catch(e) {
      console.warn("Error initializing auto rotation. If you're currently using any tool (such as the screenshotter) this error can safely be ignored", e);
    };
  }

  public stopAutoRotation = () => {
    try {
      const mount = (this.engine as any)._mount as HTMLElement;
      mount.removeEventListener('click', this.stopAutoRotation);
      mount.removeEventListener('touchstart', this.stopAutoRotation);
      if (this._autoRotationIntervalId) window.clearInterval(this._autoRotationIntervalId);
      this._autoRotationActive = false;
    } catch(e) {
      console.warn("Error stopping auto rotation. If you're currently using any tool (such as the screenshotter) this error can safely be ignored", e);
    };
  };// /_stopAutoRotation()
  
  private _initializeAOShadow = () => {
    const scene = this.engine.services.render.scene;
    const planeGeom = new PlaneGeometry(0.06, 0.08, 1, 1).rotateX(-Math.PI/2);//.rotateY(-Math.PI/2);
    const planeMesh = new Mesh(planeGeom, new MeshBasicMaterial({color: 0xFFFF00}));
    const material = this._assetProvider.getAssets().find(a => a.info.name === "AO_ShadowMat");

    if (!material) {
      console.error("Cannot find shadow material!");
      return;
    }

    this.engine.services.material.setMaterial(planeMesh, material.id);
    scene.add(planeMesh);
    this._shadowPlane = planeMesh;
  }

  private _initializePlanarReflection = () => {
    const scene = this.engine.services.render.scene;
    const planeGeom = new PlaneGeometry(0.4, 0.4, 32, 32).rotateX(-Math.PI/2);
    const planeMesh = new Mesh(planeGeom, new MeshBasicMaterial({color: 0xFFFF00}));
    const composer = this.engine.services.render.renderController.composer;
    const renderer = this.engine.services.render.renderController.renderer;
    const reflectionCamera = new PerspectiveCamera();
    const colorRenderTarget = new WebGLRenderTarget(512, 512);
    const yFadeRenderTarget = new WebGLRenderTarget(512, 512);
    
    /* Setup reflection shader */
    const reflectionMaterial = new ShaderMaterial({
      uniforms: {
        diffuseMap: {
          value: colorRenderTarget.texture
        },
        factorMap: {
          value: yFadeRenderTarget.texture
        },
        invResolution: {
          value: new Vector2()
        }
      },
      vertexShader: require("!!raw-loader!./shader/reflection.vert.glsl").default,
      fragmentShader: require("!!raw-loader!./shader/reflection.frag.glsl").default
    });

    const yFadeMaterial = new ShaderMaterial({
      uniforms: {
      },
      vertexShader: require("!!raw-loader!./shader/fade.vert.glsl").default,
      fragmentShader: require("!!raw-loader!./shader/fade.frag.glsl").default,
    });

    const originalComposerRender = composer.render;

    (planeMesh.material as any) = reflectionMaterial;
    planeMesh.name = "planeMesh";
    this.reflectionPlane = planeMesh;

    let interleaveCounter = 0.0;

    composer.render = (delta:number) => {
      const camera = this.engine.services.render.camera;

      if (camera instanceof PerspectiveCamera) {
        reflectionCamera.copy(camera);

        /* Reflect camera around plane */
        reflectionCamera.position.y -= 2 * (camera.position.y - planeMesh.position.y);
        reflectionCamera.rotation.reorder("YZX");
        reflectionCamera.rotation.x  = 2 * Math.PI - reflectionCamera.rotation.x;

        const size = renderer.getDrawingBufferSize(new Vector2());
        colorRenderTarget.setSize(size.x/1.5, size.y/1.5);
        yFadeRenderTarget.setSize(size.x/1.5, size.y/1.5);
        reflectionMaterial.uniforms.invResolution.value.set(1/size.x, 1/size.y);

        planeMesh.visible = false;

        if ((interleaveCounter++ % 2) == 0) {
          /* Render blur/fadeout strength information. */
          scene.overrideMaterial = yFadeMaterial;
          renderer.setRenderTarget(yFadeRenderTarget);
          renderer.render(scene, reflectionCamera);
          scene.overrideMaterial = null;
        } else {
          /* Render reflection texture. */
          renderer.setRenderTarget(colorRenderTarget);
          renderer.render(scene, reflectionCamera);
        }

        planeMesh.visible = this.reflectionVisible;

        /* Render original scene */
        renderer.setRenderTarget(null);
        originalComposerRender.apply(composer, [delta]);
      } else {
        originalComposerRender.apply(composer, [delta]);
      }
    };

    scene.add(planeMesh);
  }


  //FIXME: TODO: Set min camera distance based on canvas aspect ratio to always keep ring fully within canvas
  public setCameraDistance = (setTo:number) => {
    if (!this.engine) return;
    const { cameraController } = this.engine.services.render;
    if (!cameraController) return; //FIXME: TryCatch instead of this?

    const distanceMin = cameraController.cameraProperties.distance_min;
    const distanceMax = cameraController.cameraProperties.distance_max;
    setTo = Math.max(distanceMin, Math.min(setTo, distanceMax));

    const cameraPosition = new Vector3().copy(cameraController.cameraPosition);

    this.engine.services.render.camera.position.copy(cameraPosition.setLength(setTo));
  };// /setCameraDistance()

  public getCameraDistance = ():number => {
    if (!this.engine) return 0;
    const { cameraController } = this.engine.services.render;
    if (!cameraController) return 0; //FIXME: TryCatch instead of this?
    const cameraDistance = cameraController.cameraPosition.distanceTo(cameraController.targetPosition);
    return cameraDistance;
  };

  public updateZoomSensitivity = (setTo?:number) => {
    //Update this._zoomSensitivity if value is provided
    if (setTo) this._zoomSensitivity = setTo;
    
    //If we already have an engine initialized apply this._zoomSensitivity
    if (this.engine && this.engine.services.render.cameraController) {
      (this.engine.services.render.cameraController as any)._controls.zoomSpeed = this._zoomSensitivity;
    };
  };

  public setRenderSettings = (settingsName:string) => {
    console.log("setRenderSettings", settingsName);
    const assets = this._assetProvider.getAssets();
    const asset = assets.find(a => a.infoType===AssetInfoTypes.Settings && a.info.name===settingsName);
    if (asset) this.engine.services.render.settings.setSettings(asset.info.data);
  }
};// /class